import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  InputBase,
  Snackbar,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { renameCampaign } from "@/api/campaigns";
import GiftLinksModal from "@/components/giftLinks/GiftLinksModal";
import {
  CAMPAIGN_BUILDER_ID_KEY,
  CAMPAIGN_NAME_MAX_LENGTH,
  CAMPAIGN_NAME_TEXT,
} from "@/constants/campaignBuilder";
import {
  ARCHIVE_MODAL,
  CANCEL_MODAL,
  PAUSE_MODAL,
  RESUME_MODAL,
} from "@/constants/campaignLifecycle";
import {
  CAMPAIGN_STATUSES,
  CAMPAIGN_WRITE_ERROR,
  CAUSE_BENEFICIARIES_LABEL,
} from "@/constants/campaings";
import {
  useBouncedEmails,
  useRefreshBouncedEmails,
  useResendBouncedEmails,
} from "@/hooks/useBouncedEmails";
import { type ResumeDates, useCampaignLifecycle } from "@/hooks/useCampaignLifecycle";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { usePermissions } from "@/hooks/usePermissions";
import type { Campaign } from "@/interfaces/campaing";
import {
  formatBudget,
  formatCampaignDateLabel,
  formatCampaignDistribution,
  formatDate,
} from "@/lib/campaigns";
import { apiErrorMessage } from "@/utils/apiError-helper";
import {
  campaignActionBlockedReason,
  canArchiveCampaign,
  canCancelCampaign,
  canDeleteCampaign,
  canDisableCampaign,
  canEnableCampaign,
  canPauseCampaign,
  canRestoreCampaign,
  canResumeCampaign,
  canShowEdit,
  formatCauseSummary,
  pausedSettleDate,
  resumeNeedsDates,
} from "@/utils/campaign-gating-helper";
import { invalidateCampaignReads } from "@/utils/campaignCache-helper";
import { isValidCampaignName, normalizeCampaignName } from "@/utils/campaignName-helper";
import { writeDenialMessage } from "@/utils/saveError-helper";
import BouncedEmailsModal from "./BouncedEmailsModal";
import CampaignActionButtons from "./CampaignActionButtons";
import CampaignAnalyticsPanel from "./CampaignAnalyticsPanel";
import CampaignStatusChip from "./CampaignStatusChip";
import CauseBeneficiaryModal from "./CauseBeneficiaryModal";
import ArchiveCampaignModal from "./lifecycle/ArchiveCampaignModal";
import CancelCampaignModal from "./lifecycle/CancelCampaignModal";
import DeleteCampaignModal from "./lifecycle/DeleteCampaignModal";
import PauseCampaignModal from "./lifecycle/PauseCampaignModal";
import ResumeCampaignModal from "./lifecycle/ResumeCampaignModal";
import ManageContactsModal from "./ManageContactsModal";
import { contactsSurface } from "./moreOptions-helper";
import RedeemQrModal from "./RedeemQrModal";
import ReminderEmailsModal from "./ReminderEmailsModal";

interface CampaignCardProps {
  campaign: Campaign;
  analyticsOpen?: boolean;
  onAnalyticsToggle?: () => void;
  onClone?: (campaign: Campaign) => void;
  onDelete?: (campaign: Campaign) => void;
  isLoading?: boolean;
  /**
   * Report a card action the API refused (currently rename — clone/delete are owned by the list
   * page). The parent shows it as a toast; omitted usages just revert silently as before.
   */
  onActionError?: (message: string) => void;
}

export default function CampaignCard({
  campaign,
  analyticsOpen = false,
  onAnalyticsToggle,
  onClone,
  onDelete,
  isLoading = false,
  onActionError,
}: CampaignCardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isGfg, can } = usePermissions();
  // THIS campaign, not the role: a caller who edits in one group may only view another's.
  const canEditCampaign = campaign.allowedActions.edit;
  const canSendCampaign = canEditCampaign && can("send", "CAMPAIGN");
  const isMobile = useIsMobile();
  const [contactsModalOpen, setContactsModalOpen] = useState(false);
  const [giftLinksOpen, setGiftLinksOpen] = useState(false);
  const [redeemQrOpen, setRedeemQrOpen] = useState(false);
  // Which surface the contacts item names and opens: gift links (manual), contacts, or none
  // (a Universal Link).
  const surface = contactsSurface(campaign.distribution);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const { data: bouncedEmails = [], isLoading: bouncedEmailsLoading } = useBouncedEmails(
    campaign.id,
    campaign.alertCount > 0,
  );
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [causeModalOpen, setCauseModalOpen] = useState(false);

  const { mutateAsync: resendBouncedEmails } = useResendBouncedEmails(campaign.id);
  const refreshBouncedEmails = useRefreshBouncedEmails(campaign.id);

  const handleResend = (entries: { contactId: string; newEmail: string }[]) =>
    resendBouncedEmails(entries);

  // Rename state
  const [localName, setLocalName] = useState(campaign.name);
  const [isRenaming, setIsRenaming] = useState(false);
  const [editName, setEditName] = useState(campaign.name);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);

  // ── Lifecycle actions (pause / resume / cancel / archive / restore / delete) ──
  const lifecycle = useCampaignLifecycle(campaign.id);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: "success" | "error" } | null>(
    null,
  );

  const canPause = canPauseCampaign(campaign);
  const canResume = canResumeCampaign(campaign);
  const canDisable = canDisableCampaign(campaign);
  const canEnable = canEnableCampaign(campaign);
  const canCancel = canCancelCampaign(campaign);
  const canArchive = canArchiveCampaign(campaign);
  const canRestore = canRestoreCampaign(campaign);
  const canDelete = canDeleteCampaign(campaign);
  const blockedReason = campaignActionBlockedReason(campaign, formatDate);
  // Derived (was local state): a paused campaign carries status "paused", so the ACTIVE-only reminder
  // sub-line below never shows its "Reminders paused" branch — the Paused chip + settle line cover it.
  const isPaused = campaign.status === CAMPAIGN_STATUSES.PAUSED;

  const lifecycleBusy =
    lifecycle.pause.isPending ||
    lifecycle.resume.isPending ||
    lifecycle.cancel.isPending ||
    lifecycle.archive.isPending ||
    lifecycle.restore.isPending ||
    lifecycle.disable.isPending ||
    lifecycle.enable.isPending;

  function showToast(message: string, severity: "success" | "error" = "success") {
    setToast({ message, severity });
  }

  // A LIVE campaign gets the explainer dialog; a scheduled one pauses silently (nothing has sent).
  function handlePause() {
    if (campaign.status === CAMPAIGN_STATUSES.ACTIVE) {
      setPauseOpen(true);
      return;
    }
    void runPause();
  }
  async function runPause() {
    setPauseOpen(false);
    try {
      await lifecycle.pause.mutateAsync();
      showToast(PAUSE_MODAL.toast(campaign.name));
    } catch (err) {
      showToast(apiErrorMessage(err), "error");
    }
  }

  // Open the forced date picker when the window lapsed; otherwise resume straight away.
  function handleResume() {
    if (resumeNeedsDates(campaign)) {
      setResumeOpen(true);
      return;
    }
    void runResume();
  }
  async function runResume(dates?: ResumeDates) {
    try {
      await lifecycle.resume.mutateAsync(dates);
      setResumeOpen(false);
      showToast(
        dates
          ? RESUME_MODAL.toastRescheduled(campaign.name)
          : RESUME_MODAL.toastResumed(campaign.name),
      );
    } catch (err) {
      // The server 422s a plain resume when dates lapsed — fall back to the picker.
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 422 && !dates) {
        setResumeOpen(true);
        return;
      }
      showToast(apiErrorMessage(err), "error");
    }
  }

  // Universal Link: toggle the shared /u/ link off/on. Reversible + no credit/data impact, so it fires
  // directly (no confirmation dialog, unlike Pause/Cancel) with a toast.
  async function handleDisable() {
    try {
      await lifecycle.disable.mutateAsync();
      showToast(`Disabled: ${campaign.name}. Your universal link is now off.`);
    } catch (err) {
      showToast(apiErrorMessage(err), "error");
    }
  }
  async function handleEnable() {
    try {
      await lifecycle.enable.mutateAsync();
      showToast(`Enabled: ${campaign.name}. Your universal link is live again.`);
    } catch (err) {
      showToast(apiErrorMessage(err), "error");
    }
  }

  // Copy the public /u/ link to share. Present only for a universal campaign that has one.
  function handleCopyUniversalLink() {
    if (!campaign.universalToken) return;
    navigator.clipboard
      .writeText(`${window.location.origin}/u/${campaign.universalToken}`)
      .then(() => showToast("Universal link copied to clipboard"))
      .catch(() => {});
  }

  async function runCancel() {
    setCancelOpen(false);
    try {
      await lifecycle.cancel.mutateAsync();
      showToast(CANCEL_MODAL.toast(campaign.name));
    } catch (err) {
      showToast(apiErrorMessage(err), "error");
    }
  }

  async function runArchive() {
    setArchiveOpen(false);
    try {
      await lifecycle.archive.mutateAsync();
      showToast(ARCHIVE_MODAL.toast(campaign.name));
    } catch (err) {
      showToast(apiErrorMessage(err), "error");
    }
  }

  // Un-archive — no dialog, per the prototype.
  async function handleRestore() {
    try {
      await lifecycle.restore.mutateAsync();
      showToast(`Restored: ${campaign.name}`);
    } catch (err) {
      showToast(apiErrorMessage(err), "error");
    }
  }

  // Delete keeps the parent's optimistic removal (onDelete); the modal is just the confirmation.
  function handleDeleteConfirm() {
    setDeleteOpen(false);
    onDelete?.(campaign);
  }

  // A paused-past-expiry card shows when it will auto-settle (expiry + 30 days): paused-from-active
  // closes, paused-from-scheduled cancels. Before expiry the Paused chip speaks for itself.
  const today = new Date().toISOString().slice(0, 10);
  const pausedSettleLabel =
    campaign.status === CAMPAIGN_STATUSES.PAUSED && campaign.endDate.slice(0, 10) < today
      ? `${campaign.pausedFrom === CAMPAIGN_STATUSES.SCHEDULED ? "Cancels" : "Closes"} automatically ${formatDate(
          pausedSettleDate(campaign.endDate).toISOString(),
        )} unless resumed`
      : null;

  // Universal Link has one shared link and no per-recipient list; used to gate the Copy-link action.
  // (The "Manage Contacts" item itself hides via the API-driven `contactsMode`/`surface` = null.)
  const isUniversal = campaign.notificationMode === "universal";

  const nameTooLong = editName.trim() !== "" && !isValidCampaignName(editName);

  function handleRenameStart() {
    setEditName(localName);
    setIsRenaming(true);
  }

  async function handleRenameSave() {
    if (savingRef.current) return;
    const trimmed = normalizeCampaignName(editName);
    // An over-cap name (a legacy or cloned one) stays open with the error instead of a doomed PATCH.
    if (trimmed && !isValidCampaignName(trimmed)) return;
    setIsRenaming(false);
    if (!trimmed || trimmed === localName) return;

    const previousName = localName;
    savingRef.current = true;
    setLocalName(trimmed);
    setIsSaving(true);
    try {
      await renameCampaign(campaign.id, trimmed);
      // `localName` is seeded from the cached row only at mount, so a stale cache would bring the
      // old name back the next time My Campaigns opens.
      invalidateCampaignReads(queryClient);
    } catch (err) {
      setLocalName(previousName);
      onActionError?.(writeDenialMessage(err, CAMPAIGN_WRITE_ERROR.rename));
    } finally {
      setIsSaving(false);
      savingRef.current = false;
    }
  }

  function handleRenameKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") handleRenameSave();
    if (e.key === "Escape") setIsRenaming(false);
  }

  // Edit / Preview both open THIS campaign in the builder. The builder provider (scoped to
  // /campaign/create) reads the stashed id on mount and loads it — so we stash the id and route to
  // the step we want (index = collection, /design?preview=1 auto-opens the fullscreen recipient
  // preview). Manage Contacts no longer routes here: it opens a read-only modal on the card.
  function openInBuilder(subPath: string) {
    sessionStorage.setItem(CAMPAIGN_BUILDER_ID_KEY, String(campaign.id));
    navigate(`/campaign/create${subPath}`);
  }

  // Preview branches by edit access to this campaign. An editor enters the builder's fullscreen
  // recipient preview (?preview=1). Anyone who may only view it has no business inside the builder
  // — and "Exit" there strands them on Step 3 — so they open the campaign's demo link (/g/:token)
  // in a NEW TAB and just close it to return. previewToken is null only for legacy campaigns with no
  // mock link; the item is hidden for viewers in that case (see canPreview below), so this branch
  // never no-ops silently.
  function handlePreview() {
    if (canEditCampaign) {
      openInBuilder("/design?preview=1");
      return;
    }
    if (campaign.previewToken) {
      window.open(
        `${window.location.origin}/g/${campaign.previewToken}`,
        "_blank",
        "noopener,noreferrer",
      );
    }
  }

  // Editors always preview (via the builder); viewers only when there is a demo link to open.
  const canPreview = canEditCampaign || Boolean(campaign.previewToken);

  // A minimal draft (budget selected, no cause yet) has no beneficiary — hide the Cause
  // Beneficiaries block rather than render an empty circle + broken icon. Must stay in sync with
  // `causeSummary` below: the API fills `impactPledgeOrg` (beneficiary_vendor) and `impactPledge`
  // (beneficiary_short_desc) from independently-nullable columns, so gating on the phrase alone
  // would hide the block for a beneficiary that has a name to show.
  const hasImpact = Boolean(
    campaign.impactPledgeOrg || campaign.impactPledge || campaign.impactPledgeIconUrl,
  );

  // The inline summary names the ORGANIZATION ("Comfort Cases"), not the impact sentence
  // ("Provides blankets to children entering foster care") — `impactPledge` holds the phrase, so it
  // is only a fallback for campaigns persisted before `impactPledgeOrg` existed.
  const causeSummary = formatCauseSummary(
    campaign.impactPledgeOrg || campaign.impactPledge,
    campaign.causeCount,
  );

  const dateLabel = formatCampaignDateLabel(campaign);

  const durationLabel =
    campaign.status === CAMPAIGN_STATUSES.CLOSED
      ? "Completed"
      : campaign.status === CAMPAIGN_STATUSES.ACTIVE && campaign.daysLeft != null
        ? `${campaign.daysLeft} days left`
        : `Runs for ${campaign.durationDays} days`;

  const showProgressBar =
    campaign.status === CAMPAIGN_STATUSES.ACTIVE || campaign.status === CAMPAIGN_STATUSES.CLOSED;

  /**
   * The campaign's own card has been declined — shown from the FIRST failure, beside the status.
   *
   * The banner tells a client something is wrong wherever they are; this tells them WHICH campaign
   * without making them read the banner's sentence. Two states, because they need different things
   * from the reader: a decline that is still being retried, and one where collection has stopped and
   * nothing happens until they act.
   */
  const fundingChip =
    // Same audience as the banner: the client's own people, and only those who can act. GFG staff
    // read this off the Actions dashboard instead — a client's payment trouble on a GFG screen is
    // another organisation's business. Edit is checked on THIS campaign: the card change it leads to
    // is refused outside the caller's groups.
    !isGfg && campaign.allowedActions.edit && (campaign.topup?.failedAttempts ?? 0) > 0 ? (
      <Chip
        size="small"
        label={campaign.topup?.stopped ? "Funding suspended" : "Payment declined"}
        // MUI makes a chip with an onClick focusable and Enter/Space-activated, but its label says
        // the STATE, not what activating it does — so the accessible name says both.
        aria-label={`${
          campaign.topup?.stopped ? "Funding suspended" : "Payment declined"
        } — update the payment card for ${campaign.name}`}
        onClick={() => navigate(`/campaigns/${campaign.id}/payment-card`)}
        // One ramp, two weights: filled Good Wine once collection has stopped, its tint while the
        // retries are still running.
        sx={
          campaign.topup?.stopped
            ? { cursor: "pointer", bgcolor: "var(--color-good-wine)", color: "var(--color-white)" }
            : {
                cursor: "pointer",
                bgcolor: "var(--color-good-wine-bg)",
                color: "var(--color-good-wine)",
                border: "1px solid var(--color-good-wine-4)",
              }
        }
      />
    ) : null;

  const actionButtons = (
    <CampaignActionButtons
      alertCount={campaign.alertCount}
      onNotifications={() => setReminderModalOpen(true)}
      onWarning={() => setAlertModalOpen(true)}
      analyticsActive={analyticsOpen}
      onAnalytics={onAnalyticsToggle}
      onRename={handleRenameStart}
      onClone={() => onClone?.(campaign)}
      onEdit={() => openInBuilder("")}
      onManageContacts={() =>
        surface === "giftLinks" ? setGiftLinksOpen(true) : setContactsModalOpen(true)
      }
      onPreview={handlePreview}
      onDelete={() => setDeleteOpen(true)}
      onPause={handlePause}
      onResume={handleResume}
      onCancel={() => setCancelOpen(true)}
      onArchive={() => setArchiveOpen(true)}
      onRestore={handleRestore}
      onDisable={handleDisable}
      onEnable={handleEnable}
      // Universal Link only: the "Copy Universal Link" item renders only when there's a link to copy.
      onCopyLink={isUniversal && campaign.universalToken ? handleCopyUniversalLink : undefined}
      // Redeem link & QR: gfg campaigns only, and only once Active (the redeem code exists from
      // creation; the QR is just not surfaced until the campaign is live).
      onRedeemQr={
        campaign.notificationMode === "gfg" && campaign.status === CAMPAIGN_STATUSES.ACTIVE
          ? () => setRedeemQrOpen(true)
          : undefined
      }
      canPreview={canPreview}
      canPause={canPause}
      canResume={canResume}
      canDisable={canDisable}
      canEnable={canEnable}
      canCancel={canCancel}
      canArchive={canArchive}
      canRestore={canRestore}
      canDelete={canDelete}
      blockedReason={blockedReason}
      canEdit={canShowEdit(campaign.status)}
      contactsMode={surface}
      allowedActions={campaign.allowedActions}
      // Only an automated campaign has a card of its own; for everything else the item does not
      // render at all, rather than rendering greyed.
      onChangeCard={
        campaign.fundingType === "automated"
          ? () => navigate(`/campaigns/${campaign.id}/payment-card`)
          : undefined
      }
      cardFailing={campaign.topup?.failedAttempts ? campaign.topup.failedAttempts > 0 : false}
    />
  );

  return (
    <>
      <Box
        sx={{
          bgcolor: "background.paper",
          border: "1px solid var(--color-smoke)",
          borderRadius: "var(--radius-card)",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {(isLoading || isSaving || lifecycleBusy) && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
            }}
          >
            <CircularProgress
              aria-label="Loading"
              size={28}
              sx={{ color: "var(--color-dark-dust)" }}
            />
          </Box>
        )}

        {isMobile ? (
          /* ── Mobile layout ── */
          <Box sx={{ px: 2.5 }}>
            {/* Header: image + name + recipients */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "var(--radius-sm)",
                  overflow: "hidden",
                  bgcolor: "var(--color-wax)",
                  flexShrink: 0,
                }}
              >
                {campaign.imageUrl && (
                  <img
                    src={campaign.imageUrl}
                    alt={localName}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-around",
                  alignSelf: "stretch",
                }}
              >
                {isRenaming ? (
                  <div className="min-w-0">
                    <InputBase
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={handleRenameKeyDown}
                      onBlur={handleRenameSave}
                      autoFocus
                      fullWidth
                      inputProps={{
                        maxLength: CAMPAIGN_NAME_MAX_LENGTH,
                        "aria-invalid": nameTooLong,
                      }}
                      sx={{
                        fontWeight: 600,
                        fontSize: "1rem",
                        lineHeight: 1.3,
                        color: "var(--color-rodeo-black)",
                        "& .MuiInputBase-input": {
                          p: 0,
                          borderBottom: "2px solid var(--color-dark-dust)",
                        },
                      }}
                    />
                    <span
                      className={`block font-lato text-[12px] ${nameTooLong ? "text-good-wine" : "text-graphite"}`}
                    >
                      {editName.length} / {CAMPAIGN_NAME_MAX_LENGTH}
                      {nameTooLong ? ` — ${CAMPAIGN_NAME_TEXT.tooLong}` : ""}
                    </span>
                  </div>
                ) : (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: "var(--color-rodeo-black)",
                      fontWeight: 600,
                      lineHeight: 1.3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {localName}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: "var(--color-graphite)" }}>
                  {campaign.recipientCount} recipients · {formatCampaignDistribution(campaign)}
                </Typography>
              </Box>
            </Box>

            {/* Budget + Cause Beneficiaries side by side */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 1.5, flexShrink: 0 }}
              >
                <Typography
                  variant="overline"
                  sx={{ color: "var(--color-graphite)", lineHeight: 1 }}
                >
                  Budget
                </Typography>
                <Box
                  sx={{ display: "flex", gap: 0.5, alignItems: "baseline", whiteSpace: "nowrap" }}
                >
                  <Typography
                    variant="body1"
                    sx={{ color: "var(--color-rodeo-black)", fontWeight: 400 }}
                  >
                    {formatBudget(campaign.budget)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--color-graphite)" }}>
                    ({formatBudget(campaign.perGiftValue)} / gift)
                  </Typography>
                </Box>
              </Box>

              {hasImpact && (
                <Box
                  component="button"
                  type="button"
                  onClick={() => setCauseModalOpen(true)}
                  aria-haspopup="dialog"
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    py: 1.5,
                    px: 0.5,
                    borderRadius: "var(--radius-sm)",
                    border: 0,
                    bgcolor: "transparent",
                    font: "inherit",
                    color: "inherit",
                    textAlign: "left",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "var(--color-wax)" },
                    "&:focus-visible": {
                      outline: "2px solid var(--color-dark-dust)",
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: isMobile ? 38 : 32,
                      height: isMobile ? 38 : 32,
                      minWidth: 32,
                      borderRadius: "50%",
                      bgcolor: "var(--color-good-sage-5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      flexShrink: 0,
                      p: 0.5,
                    }}
                  >
                    <img
                      src={campaign.impactPledgeIconUrl}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  </Box>
                  <Box component="span" sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Typography
                      variant="overline"
                      sx={{ color: "var(--color-graphite)", lineHeight: 1 }}
                    >
                      {CAUSE_BENEFICIARIES_LABEL}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "var(--color-graphite)" }}>
                      {causeSummary}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Status chip + Dates */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                py: 1,
              }}
            >
              <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1 }}>
                <CampaignStatusChip status={campaign.status} showDot={false} size="md" />
                {fundingChip}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  alignItems: "flex-start",
                }}
              >
                <Typography variant="caption" sx={{ color: "var(--color-graphite)" }}>
                  {dateLabel}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      campaign.status === CAMPAIGN_STATUSES.CLOSED
                        ? "var(--color-good-sage)"
                        : "var(--color-rodeo-black)",
                    fontWeight: 400,
                  }}
                >
                  {durationLabel}
                </Typography>
                {pausedSettleLabel && (
                  <Typography variant="caption" sx={{ color: "var(--color-good-wine)" }}>
                    {pausedSettleLabel}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Progress bar + reminder (active / closed) */}
            {showProgressBar && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, pb: 1.5 }}>
                <Box
                  sx={{
                    height: 6,
                    borderRadius: 999,
                    bgcolor: "var(--color-wax)",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: `${campaign.progress}%`,
                      bgcolor: "var(--color-dark-dust)",
                      borderRadius: 999,
                      transition: "width 0.3s ease",
                    }}
                  />
                </Box>
                {campaign.status === CAMPAIGN_STATUSES.ACTIVE &&
                  (isPaused || campaign.reminderDaysLeft != null) && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <NotificationsNoneIcon
                        sx={{ fontSize: 14, color: "var(--color-graphite)" }}
                      />
                      <Typography variant="caption" sx={{ color: "var(--color-graphite)" }}>
                        {isPaused
                          ? "Reminders paused"
                          : `Next reminder sends in ${campaign.reminderDaysLeft} days`}
                      </Typography>
                    </Box>
                  )}
              </Box>
            )}

            {/* Action buttons — bottom strip */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                borderTop: "1px solid var(--color-smoke)",
                py: 1.5,
              }}
            >
              {actionButtons}
            </Box>
          </Box>
        ) : (
          /* ── Desktop layout ── */
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              px: 2.5,
              minHeight: 162,
            }}
          >
            {/* Left section: image + info */}
            <Box
              sx={{
                display: "flex",
                flex: 1,
                minWidth: 0,
                minHeight: 120,
                alignItems: "center",
                borderRight: "1px solid var(--color-smoke)",
                pr: 3,
                py: 2,
              }}
            >
              <Box
                sx={{
                  width: 120,
                  minWidth: 120,
                  alignSelf: "stretch",
                  bgcolor: "var(--color-wax)",
                  borderRadius: "var(--radius-sm)",
                  mr: 3,
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                {campaign.imageUrl && (
                  <img
                    src={campaign.imageUrl}
                    alt={localName}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                )}
              </Box>

              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 0.75, flex: 1, minWidth: 0 }}
              >
                {isRenaming ? (
                  <div className="min-w-0">
                    <InputBase
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={handleRenameKeyDown}
                      onBlur={handleRenameSave}
                      autoFocus
                      fullWidth
                      inputProps={{
                        maxLength: CAMPAIGN_NAME_MAX_LENGTH,
                        "aria-invalid": nameTooLong,
                      }}
                      sx={{
                        fontWeight: 600,
                        fontSize: "1rem",
                        lineHeight: 1.3,
                        color: "var(--color-rodeo-black)",
                        "& .MuiInputBase-input": {
                          p: 0,
                          borderBottom: "2px solid var(--color-dark-dust)",
                        },
                      }}
                    />
                    <span
                      className={`block font-lato text-[12px] ${nameTooLong ? "text-good-wine" : "text-graphite"}`}
                    >
                      {editName.length} / {CAMPAIGN_NAME_MAX_LENGTH}
                      {nameTooLong ? ` — ${CAMPAIGN_NAME_TEXT.tooLong}` : ""}
                    </span>
                  </div>
                ) : (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: "var(--color-rodeo-black)",
                      fontWeight: 600,
                      lineHeight: 1.3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {localName}
                  </Typography>
                )}

                <Typography variant="body2" sx={{ color: "var(--color-graphite)" }}>
                  {campaign.recipientCount} recipients · {formatCampaignDistribution(campaign)}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                    mt: 0.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Typography
                      variant="overline"
                      sx={{ color: "var(--color-graphite)", lineHeight: 1 }}
                    >
                      Budget
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "baseline" }}>
                      <Typography
                        variant="body1"
                        sx={{ color: "var(--color-rodeo-black)", fontWeight: 400 }}
                      >
                        {formatBudget(campaign.budget)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "var(--color-graphite)" }}>
                        ({formatBudget(campaign.perGiftValue)} / gift)
                      </Typography>
                    </Box>
                  </Box>

                  {hasImpact && (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setCauseModalOpen(true)}
                      aria-haspopup="dialog"
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        p: 1,
                        borderRadius: "var(--radius-sm)",
                        border: 0,
                        bgcolor: "transparent",
                        font: "inherit",
                        color: "inherit",
                        textAlign: "left",
                        cursor: "pointer",
                        "&:hover": { bgcolor: "var(--color-wax)" },
                        "&:focus-visible": {
                          outline: "2px solid var(--color-dark-dust)",
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          width: 36,
                          height: 36,
                          minWidth: 36,
                          borderRadius: "50%",
                          bgcolor: "var(--color-good-sage-5)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          flexShrink: 0,
                          p: 0.625,
                        }}
                      >
                        <img
                          src={campaign.impactPledgeIconUrl}
                          alt=""
                          style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      </Box>
                      <Box
                        component="span"
                        sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                      >
                        <Typography
                          variant="overline"
                          sx={{ color: "var(--color-graphite)", lineHeight: 1 }}
                        >
                          {CAUSE_BENEFICIARIES_LABEL}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "var(--color-graphite)" }}>
                          {causeSummary}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Right section: status + actions + dates + progress */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                pl: 3,
                py: 2,
                minWidth: 380,
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <CampaignStatusChip status={campaign.status} showDot={false} size="md" />
                {fundingChip}
                {actionButtons}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: "var(--color-graphite)" }}>
                  {dateLabel}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      campaign.status === CAMPAIGN_STATUSES.CLOSED
                        ? "var(--color-good-sage)"
                        : "var(--color-rodeo-black)",
                    fontWeight: 400,
                  }}
                >
                  {durationLabel}
                </Typography>
                {pausedSettleLabel && (
                  <Typography variant="caption" sx={{ color: "var(--color-good-wine)" }}>
                    {pausedSettleLabel}
                  </Typography>
                )}
              </Box>

              {showProgressBar && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  <Box
                    sx={{
                      height: 6,
                      borderRadius: 999,
                      bgcolor: "var(--color-wax)",
                      overflow: "hidden",
                      width: "100%",
                      maxWidth: 200,
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${campaign.progress}%`,
                        bgcolor: "var(--color-dark-dust)",
                        borderRadius: 999,
                        transition: "width 0.3s ease",
                      }}
                    />
                  </Box>

                  {campaign.status === CAMPAIGN_STATUSES.ACTIVE &&
                    (isPaused || campaign.reminderDaysLeft != null) && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <NotificationsNoneIcon
                          sx={{ fontSize: 14, color: "var(--color-graphite)" }}
                        />
                        <Typography variant="caption" sx={{ color: "var(--color-graphite)" }}>
                          {isPaused
                            ? "Reminders paused"
                            : `Next reminder sends in ${campaign.reminderDaysLeft} days`}
                        </Typography>
                      </Box>
                    )}
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Collapse in={analyticsOpen} unmountOnExit>
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <CampaignAnalyticsPanel
              campaign={campaign}
              onFixBounces={
                canEditCampaign && campaign.alertCount > 0
                  ? () => setAlertModalOpen(true)
                  : undefined
              }
            />
          </Box>
        </Collapse>
      </Box>

      <BouncedEmailsModal
        open={alertModalOpen}
        onClose={() => {
          setAlertModalOpen(false);
          refreshBouncedEmails();
        }}
        bouncedEmails={bouncedEmails}
        loading={bouncedEmailsLoading}
        onResend={handleResend}
        canResend={
          campaign.status === CAMPAIGN_STATUSES.ACTIVE ||
          campaign.status === CAMPAIGN_STATUSES.SCHEDULED
        }
      />
      <ManageContactsModal
        open={contactsModalOpen}
        onClose={() => setContactsModalOpen(false)}
        campaignId={campaign.id}
        campaignName={campaign.name}
        pricePerGift={campaign.perGiftValue}
        campaignStatus={campaign.status}
        manual={campaign.notificationMode === "manual"}
        paused={isPaused}
        canEdit={canEditCampaign}
        canSend={canSendCampaign}
      />
      {surface === "giftLinks" && (
        <GiftLinksModal
          open={giftLinksOpen}
          onClose={() => setGiftLinksOpen(false)}
          campaignId={campaign.id}
          campaignName={campaign.name}
          pricePerGift={campaign.perGiftValue}
          campaignStatus={campaign.status}
          paused={isPaused}
          canEdit={canEditCampaign}
        />
      )}
      <RedeemQrModal
        open={redeemQrOpen}
        onClose={() => setRedeemQrOpen(false)}
        campaignName={campaign.name}
        redeemCode={campaign.redeemCode}
      />
      <ReminderEmailsModal
        open={reminderModalOpen}
        onClose={() => setReminderModalOpen(false)}
        campaignId={campaign.id}
        canEdit={canEditCampaign}
      />
      {hasImpact && (
        <CauseBeneficiaryModal
          open={causeModalOpen}
          onClose={() => setCauseModalOpen(false)}
          campaign={campaign}
        />
      )}

      <PauseCampaignModal
        open={pauseOpen}
        campaignName={campaign.name}
        onClose={() => setPauseOpen(false)}
        onConfirm={runPause}
      />
      <ResumeCampaignModal
        open={resumeOpen}
        campaign={campaign}
        submitting={lifecycle.resume.isPending}
        onClose={() => setResumeOpen(false)}
        onConfirm={runResume}
      />
      <CancelCampaignModal
        open={cancelOpen}
        campaignId={campaign.id}
        campaignName={campaign.name}
        onClose={() => setCancelOpen(false)}
        onConfirm={runCancel}
      />
      <ArchiveCampaignModal
        open={archiveOpen}
        campaignName={campaign.name}
        wasCanceled={campaign.status === CAMPAIGN_STATUSES.CANCELED}
        onClose={() => setArchiveOpen(false)}
        onConfirm={runArchive}
      />
      <DeleteCampaignModal
        open={deleteOpen}
        campaignName={campaign.name}
        wasCanceled={campaign.status === CAMPAIGN_STATUSES.CANCELED}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        // Top-center per the lifecycle prototype, but the app's own filled Alert (color + shape),
        // matching every other toast in the product (e.g. CampaignContactsModal).
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: { xs: 70, sm: 80 } }}
      >
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
